import { useEffect, useState } from "react";
import api, { extractError } from "@/api/client";
import { X, MessageCircle, Send, AlertCircle, Check, Phone } from "lucide-react";

/**
 * Admin diagnostic panel for Twilio WhatsApp integration.
 *
 * Shows current sender config + provides a one-off test-send form so admins
 * can verify their WhatsApp Business sender is approved and reachable from a
 * real browser instead of hitting curl.
 */
export default function TwilioSettingsModal({ onClose }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancel = false;
    api.get("/admin/twilio/status").then(({ data }) => {
      if (!cancel) setStatus(data);
    }).catch(() => { if (!cancel) setStatus({ configured: false }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const send = async () => {
    setErr(""); setResult(null);
    if (!phone.trim()) { setErr("أدخل رقم الهاتف"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/twilio/test-send", {
        phone: phone.trim(),
        message: message.trim() || undefined,
      });
      setResult(data);
    } catch (e) { setErr(extractError(e) || "فشل الإرسال"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="twilio-settings-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col" style={{maxHeight:"92vh"}}>
        <div className="flex justify-between items-center p-4 border-b border-[var(--border)] bg-gradient-to-l from-[#25D366] to-[#128C7E] text-white">
          <div className="flex items-center gap-2">
            <MessageCircle size={20}/>
            <h2 className="text-lg font-black" style={{fontFamily:'Cairo'}}>إعدادات WhatsApp</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" data-testid="twilio-settings-close"><X size={16}/></button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {/* Status */}
          {loading ? (
            <div className="text-center text-sm text-[var(--text-secondary)] py-4">جارٍ التحميل...</div>
          ) : (
            <div className={`rounded-2xl p-4 ${status?.configured ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`} data-testid="twilio-status">
              <div className="flex items-center gap-2 mb-1">
                {status?.configured ? (
                  <><Check size={16} className="text-emerald-600"/><b className="text-emerald-700">Twilio مُفعّل</b></>
                ) : (
                  <><AlertCircle size={16} className="text-red-600"/><b className="text-red-700">Twilio غير مُعدّ</b></>
                )}
              </div>
              {status?.configured && (
                <div className="text-xs text-[var(--text-secondary)] space-y-1 mt-2">
                  {status.verify_configured && (
                    <div className="flex items-center gap-1 text-emerald-700">
                      <Check size={11}/> <b>Twilio Verify V2 مُفعّل</b> (SID: <span className="font-mono">…{status.verify_service_sid_last4}</span>)
                    </div>
                  )}
                  {status.sender && (
                    <div className="flex items-center gap-1"><Phone size={11}/> WhatsApp Sender: <b className="font-mono text-[var(--text-primary)]">{status.sender}</b></div>
                  )}
                  {status.sms_sender && (
                    <div className="flex items-center gap-1"><Phone size={11}/> SMS Sender: <b className="font-mono text-[var(--text-primary)]">{status.sms_sender}</b></div>
                  )}
                  <div>Account SID: <span className="font-mono">…{status.account_sid_last4}</span></div>
                </div>
              )}
              {!status?.configured && (
                <div className="text-xs text-[var(--text-secondary)] mt-1 space-y-1">
                  <div>أضف هذه المتغيرات في إعدادات النشر (Emergent → Deployments → Environment Variables):</div>
                  <div><code className="font-mono">TWILIO_ACCOUNT_SID</code></div>
                  <div><code className="font-mono">TWILIO_AUTH_TOKEN</code></div>
                  <div><code className="font-mono">TWILIO_VERIFY_SERVICE_SID</code> <span className="text-emerald-700">(للحل الحديث Verify V2 — موصى به)</span></div>
                  <div className="text-[10px] opacity-70 mt-1">ثم اضغط زر Redeploy لتفعيل التغييرات.</div>
                </div>
              )}
            </div>
          )}

          {/* Test send */}
          {status?.configured && (
            <div className="space-y-3">
              <div className="text-sm font-black" style={{fontFamily:'Cairo'}}>إرسال رسالة اختبار</div>
              <div>
                <label className="text-xs text-[var(--text-secondary)] mb-1 block">رقم الهاتف (E.164)</label>
                <input
                  type="tel" inputMode="tel"
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="+218900000000"
                  className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-emerald-500 focus:outline-none font-mono"
                  data-testid="twilio-test-phone"
                />
              </div>
              <div>
                <label className="text-xs text-[var(--text-secondary)] mb-1 block">الرسالة (اختياري)</label>
                <textarea
                  value={message} onChange={(e) => setMessage(e.target.value)}
                  rows={3} maxLength={500}
                  placeholder="اتركها فارغة لاستخدام رسالة الاختبار الافتراضية"
                  className="w-full p-3 rounded-xl border border-[var(--border)] focus:border-emerald-500 focus:outline-none resize-none"
                  data-testid="twilio-test-message"
                />
              </div>
              {err && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="twilio-test-err">
                  <AlertCircle size={16} className="shrink-0 mt-0.5"/> {err}
                </div>
              )}
              {result && (
                <div className={`p-3 rounded-xl border text-sm ${result.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="twilio-test-result">
                  {result.ok ? (
                    <div className="flex items-start gap-2">
                      <Check size={16} className="shrink-0 mt-0.5"/>
                      <div>
                        <b>تم الإرسال بنجاح</b>
                        <div className="text-xs mt-1">Message SID: <span className="font-mono">{result.reason}</span></div>
                        <div className="text-xs">إلى: <span className="font-mono">{result.sent_to}</span></div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <AlertCircle size={16} className="shrink-0 mt-0.5"/>
                      <div>
                        <b>لم يُرسل</b>
                        <div className="text-xs mt-1">السبب: <span className="font-mono">{result.reason}</span></div>
                        {result.reason?.includes("63007") && (
                          <div className="text-xs mt-1">💡 الرقم ليس مسجّلاً على WhatsApp أو رقم الإرسال غير مُعتمد</div>
                        )}
                        {result.reason?.includes("21211") && (
                          <div className="text-xs mt-1">💡 صيغة الرقم غير صالحة — استخدم E.164 مثل +218912345678</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={send} disabled={busy}
                className="w-full py-3 rounded-xl bg-[#25D366] text-white font-black flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-[#128C7E] transition"
                data-testid="twilio-test-send"
              >
                <Send size={16}/> {busy ? "جارٍ الإرسال..." : "إرسال الرسالة"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
